import {
  Box,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";

import { EditFieldButton } from "@/shared/components/EditFieldButton";
import { ContainerParameters } from "@/shared/domain/actions";

import { ActionContainerParams } from "./ActionContainerParams";
import { ActionContainerParamsTable } from "./ActionContainerParamsTable";
import { parseContainerParameters, stringifyCommand } from "./actionUtils";
import { ClearFieldButton } from "./ClearFieldButton";

interface EditableContainerParamsProps<UpdateType> {
  title: string;
  field: keyof UpdateType;
  value: ContainerParameters;
  onChange: (updates: UpdateType) => void;
  setErrorText: (arg: string) => void;
  overrides?: boolean;
  canEdit?: boolean;
}

export default function EditableContainerParams<UpdateType>({
  title,
  field,
  value,
  onChange,
  setErrorText,
  overrides,
  canEdit,
}: EditableContainerParamsProps<UpdateType>) {
  const theme = useTheme();
  const [showEditableIcon, setShowEditableIcon] = useState(false);
  const [editable, setEditable] = useState(false);
  const [workdir, setWorkdir] = useState<string | undefined>(
    value.workdir === null ? "null" : value.workdir,
  );
  const [command, setCommand] = useState<string | undefined>(
    stringifyCommand(value.command),
  );

  let initialState: string | undefined;
  if (value.entry_point && value.entry_point.length > 0) {
    initialState = value.entry_point[0];
  } else if (value.entry_point === null) {
    initialState = "null";
  }

  const [entrypoint, setEntrypoint] = useState<string | undefined>(
    initialState,
  );
  const [envVars, setEnvVars] = useState<Record<string, string>>(
    value.env_vars ?? {},
  );

  function updateRecord(reset?: boolean) {
    const containerParameters = parseContainerParameters(
      command,
      entrypoint,
      workdir,
      envVars,
    );

    const updates = {
      [field]: reset ? null : containerParameters,
    };

    setErrorText("");
    onChange(updates as UpdateType);
  }

  return (
    <ListItem
      onMouseOver={() => {
        setShowEditableIcon(true);
      }}
      onMouseLeave={() => {
        setShowEditableIcon(false);
      }}
    >
      <ListItemText
        primaryTypographyProps={{
          component: "span",
        }}
        primary={
          <>
            <span>{title}</span>

            <EditFieldButton
              editable={editable}
              disabled={!editable && !canEdit}
              showEditableIcon={showEditableIcon}
              onClick={() => {
                if (editable) {
                  void updateRecord();
                }
                setEditable(!editable);
              }}
            />

            {overrides && (
              <ClearFieldButton
                disabled={!editable && !canEdit}
                showIcon={showEditableIcon}
                onClick={() => {
                  void updateRecord(true);
                  setEntrypoint(undefined);
                  setCommand(undefined);
                  setWorkdir(undefined);
                }}
              />
            )}

            <Box>
              {!editable ? (
                <>
                  {value && Object.keys(value).length > 0 ? (
                    <Box
                      sx={{
                        mt: theme.spacing(1),
                      }}
                    >
                      <ActionContainerParamsTable
                        workdir={workdir}
                        command={command}
                        entrypoint={entrypoint}
                        envVars={envVars}
                      />
                    </Box>
                  ) : (
                    <Typography
                      component="span"
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      None
                    </Typography>
                  )}
                </>
              ) : (
                <Box sx={{ mt: theme.spacing(0.5) }}>
                  <ActionContainerParams
                    workdir={workdir}
                    setWorkdir={setWorkdir}
                    command={command}
                    setCommand={setCommand}
                    entrypoint={entrypoint}
                    setEntrypoint={setEntrypoint}
                    envVars={envVars}
                    setEnvVars={setEnvVars}
                  />
                </Box>
              )}
            </Box>
          </>
        }
      />
    </ListItem>
  );
}
